import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Upload, message } from "antd";
import axios from "axios";
import Swal from "sweetalert2";
import Cookies from "js-cookie";
import { ImportOutlined, InboxOutlined } from "@ant-design/icons";
import * as XLSX from 'xlsx';
import { API_DOMAIN_MONITOR } from '../../config/config';

const { Dragger } = Upload;

const escapeHtml = (str) => str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

const initFormData = {
    data: [],
};

const ImportDomainModal = ({ onSuccess, t = (key) => key }) => {
    const [formData, setFormData] = useState(null);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [form] = Form.useForm();

    const layout = {
        labelCol: { span: 7 },
        wrapperCol: { span: 17 },
    };

    const formValInit = () => {
        setFormData(initFormData);
    };

    const showModal = () => {
        formValInit();
        setIsModalVisible(true);
    };

    const handleCancel = () => {
        setIsModalVisible(false);
        form.resetFields();
        setFormData(initFormData);
    };

    const handleOk = () => {
        setIsSubmitting(true);
        onFinish();
    };

    const isOkButtonProps = () => !formData || !formData.data || formData.data.length === 0;

    useEffect(() => {
        setFormData(initFormData);
    }, []);

    const uploadConfig = {
        name: 'file',
        multiple: false,
        maxCount: 1,
        accept: '.csv',
        beforeUpload(file) {
            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    const workbook = XLSX.read(new Uint8Array(e.target.result), { type: 'array' });
                    const sheetName = workbook.SheetNames[0];
                    const sheet = workbook.Sheets[sheetName];
                    const jsonData = XLSX.utils.sheet_to_json(sheet);

                    if (jsonData.length === 0) {
                        message.error(t('domain_monitor.csv_empty'));
                        return;
                    }

                    // Validate required columns
                    const requiredColumns = ['domain', 'name'];
                    const firstRow = jsonData[0];
                    const hasRequiredColumn = requiredColumns.some(col =>
                        Object.keys(firstRow).some(key => key.toLowerCase() === col.toLowerCase())
                    );

                    if (!hasRequiredColumn) {
                        message.error(t('domain_monitor.csv_required_columns'));
                        return;
                    }

                    message.success(t('domain_monitor.file_uploaded_success', { fileName: file.name }));
                    setFormData({ ...formData, data: jsonData });
                } catch (error) {
                    message.error(t('domain_monitor.csv_read_error'));
                    console.error(error);
                }
            };
            reader.readAsArrayBuffer(file);

            // Prevent default upload
            return false;
        },
        onRemove(e) {
            let newFormData = { ...formData, data: [] };
            setFormData(newFormData);
        }
    };

    const onFinish = () => {
        const apiUrl = `${API_DOMAIN_MONITOR}/v1/domains/bulk`;

        // Helper function to safely convert and trim values
        const safeTrim = (value) => {
            if (value === null || value === undefined || value === '') return '';
            return String(value).trim();
        };

        // Process the data to match the expected format
        const processedData = formData.data.map(item => ({
            domain: safeTrim(item.domain),
            name: safeTrim(item.name),
            description: safeTrim(item.description),
            website: safeTrim(item.website),
            addressLine1: safeTrim(item.addressLine1),
            addressLine2: safeTrim(item.addressLine2),
            city: safeTrim(item.city),
            stateProvince: safeTrim(item.stateProvince),
            postalCode: safeTrim(item.postalCode),
            country: safeTrim(item.country),
            email: safeTrim(item.email),
            phone: safeTrim(item.phone),
            fullName: safeTrim(item.fullName),
            externalTrackingRef: safeTrim(item.externalTrackingRef),
            checkFrequency: item.checkFrequency ? String(item.checkFrequency) : '7',
        }));

        axios
            .post(apiUrl, { domains: processedData }, {
                headers: {
                    Authorization: `Bearer ${Cookies.get("dm_token")}`,
                }
            })
            .then((response) => {
                const { data } = response;

                if (response.status !== 200 || !data || typeof data.successCount === 'undefined') {
                    throw new Error(data?.message || t('domain_monitor.unexpected_error'));
                }

                const { successCount = 0, failedCount = 0, failed = [] } = data;

                if (failedCount === 0 && successCount > 0) {
                    Swal.fire({
                        icon: "success",
                        title: t('domain_monitor.import_success'),
                        text: t('domain_monitor.import_success_message', { count: successCount }),
                        showConfirmButton: false,
                        timer: 2000,
                        onClose: () => {
                            if (onSuccess) {
                                onSuccess();
                            }
                            setIsSubmitting(false);
                            form.resetFields();
                        },
                    });
                    handleCancel();
                } else if (successCount > 0 && failedCount > 0) {
                    const failedDetails = failed.map(f => `${f.domain}: ${f.error}`).join('\n');
                    Swal.fire({
                        icon: "warning",
                        title: t('domain_monitor.import_partial'),
                        html: `${t('domain_monitor.import_partial_message', { successCount, failedCount })}<br><br>${escapeHtml(failedDetails).replace(/\n/g, '<br>')}`,
                        showConfirmButton: true,
                    });
                    if (onSuccess) {
                        onSuccess();
                    }
                    handleCancel();
                    setIsSubmitting(false);
                } else {
                    const failedDetails = failed.map(f => `${f.domain}: ${f.error}`).join('\n');
                    Swal.fire({
                        icon: "error",
                        title: t('domain_monitor.import_failed'),
                        html: failedDetails ? escapeHtml(failedDetails).replace(/\n/g, '<br>') : t('domain_monitor.unexpected_error'),
                        showConfirmButton: true,
                    });
                    setIsSubmitting(false);
                }
            })
            .catch((error) => {
                console.error(error);
                const errorData = error.response?.data;
                let errorMessage = t('domain_monitor.unexpected_error');

                if (errorData?.error_msg?.details?.details && Array.isArray(errorData.error_msg.details.details)) {
                    errorMessage = errorData.error_msg.details.details
                        .map(err => `${err.field}: ${err.message}`)
                        .join('\n');
                } else if (errorData?.error_msg?.details?.message) {
                    errorMessage = errorData.error_msg.details.message;
                } else if (errorData?.error_msg?.msg) {
                    errorMessage = errorData.error_msg.msg;
                } else if (errorData?.message) {
                    errorMessage = errorData.message;
                } else if (error.message) {
                    errorMessage = error.message;
                }

                Swal.fire({
                    icon: "error",
                    title: t('domain_monitor.error'),
                    html: escapeHtml(errorMessage).replace(/\n/g, '<br>'),
                    showConfirmButton: true,
                });
                setIsSubmitting(false);
            });
    };

    const downloadSampleCSV = () => {
        const headers = [{
            domain: 'example.com',
            name: "Bob's Burritos",
            description: 'A family-owned burrito restaurant',
            website: 'https://example.com',
            addressLine1: '123 Main Street',
            addressLine2: 'Suite 100',
            city: 'San Francisco',
            stateProvince: 'CA',
            postalCode: '94102',
            country: 'USA',
            email: 'contact@example.com',
            phone: '+1-555-123-4567',
            fullName: 'John Doe',
            externalTrackingRef: 'MERCH-12345',
            checkFrequency: '7'
        }];

        // Create csv from headers
        const worksheet = XLSX.utils.json_to_sheet(headers, { skipHeader: false });
        const csv = XLSX.utils.sheet_to_csv(worksheet);

        // Create Blob
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.href = url;
        link.download = 'sample_domain_import.csv';
        link.click();
        URL.revokeObjectURL(url);
    };

    return (
        <>
            <Button key={`btn_import`} type="link" onClick={showModal}>
                <div className="d-flex align-items-center">
                    <ImportOutlined style={{ fontSize: "18px" }} /> <div style={{ marginLeft: "4px" }}>{t('domain_monitor.import_csv')}</div>
                </div>
            </Button>
            <Modal
                title={t('domain_monitor.import_title')}
                width={600}
                visible={isModalVisible}
                onCancel={handleCancel}
                footer={[
                    <Button key="back" onClick={() => handleCancel()}>
                        {t('domain_monitor.cancel')}
                    </Button>,
                    <Button key="submit" type="primary" loading={isSubmitting} disabled={isOkButtonProps()} onClick={() => handleOk()}>
                        {isSubmitting ? t('domain_monitor.importing') : t('domain_monitor.import')}
                    </Button>,
                ]}
            >
                <Form form={form} {...layout} name="importdomain">
                    <Button key="download-sample" type="dashed" onClick={() => downloadSampleCSV()} style={{ marginBottom: 10 }}>
                        {t('domain_monitor.download_sample')}
                    </Button>
                    <Dragger {...uploadConfig}>
                        <p className="ant-upload-drag-icon">
                            <InboxOutlined />
                        </p>
                        <p className="ant-upload-text">{t('domain_monitor.upload_text')}</p>
                        <p className="ant-upload-hint">
                            {t('domain_monitor.upload_hint')}
                        </p>
                    </Dragger>
                </Form>
            </Modal>
        </>
    );
};

export default ImportDomainModal;
